import type { DocumentId } from "@curriculum-studio/core";
import { HttpClient, HttpClientRequest, HttpClientResponse } from "@effect/platform";
import { DateTime, Effect, Schema } from "effect";
import { useEffect, useRef, useState } from "react";
import { runtime } from "./runtime.ts";

// U7 (CON-1316): the generalised async-work indicator. A curriculum's document can have a
// background job writing to it right now — the exercise expander (`kind: "expand"`) or the
// l2_unit_plan generator (`kind: "curriculum-generation"`) — and both write through the SAME
// `JobRunStore` the server's `/api/curricula/job-runs` endpoint reads. This component is the one
// place that answers "is anything running against this document", generalised over `kind` rather
// than rendering one thing for an expand run and another for a generation run: the reviewer does
// not care which pipeline is busy, only that one is.
//
// `apps/web` cannot import the server's `JobRunKind`/`LaunchLifecycle`/`LaunchTask` adapter types
// (dependencies point inward; web depends only on `@curriculum-studio/core`, never on
// `apps/server`), so the wire shape is decoded through a LOCAL boundary `Schema` that mirrors the
// server's `ActiveJobRunWire` field-for-field. This is a second definition of the same shape by
// necessity, not by accident — same pattern `ExpandStatus` in `packages/core/src/sync.ts` already
// uses for the same reason.
const LaunchLifecycleWire = Schema.Literal(
  "queued",
  "pending-approval",
  "running",
  "succeeded",
  "failed",
  "cancelled",
);

const LaunchTaskWire = Schema.Struct({
  taskKey: Schema.String,
  job: Schema.String,
  state: Schema.Literal("pending", "running", "succeeded", "failed", "skipped"),
  dependsOn: Schema.Array(Schema.String),
});

const ActiveJobRunWire = Schema.Struct({
  // `"fix"` (CON-1315 QA#2/#3b): the rail's `[Fix]`/`[Fix all]` agentic dispatch bookkeeps itself in
  // the same `JobRunStore` (`agent-turn-route.ts`'s `setupFixRunBookkeeping`) this indicator already
  // reads. `ActiveJobRuns` decodes the WHOLE array in one call (`schemaBodyJson`), so omitting a kind
  // the server can now emit doesn't just hide that one run — it fails the decode for every run on the
  // document, and `fetchActiveJobRuns`'s fail-closed `catchAll` then reports NOTHING running at all.
  kind: Schema.Literal("expand", "launch", "curriculum-generation", "fix", "enrichment-publish"),
  lifecycle: LaunchLifecycleWire,
  tasks: Schema.Array(LaunchTaskWire),
  startedAt: Schema.DateTimeUtc,
});

export type ActiveJobRun = typeof ActiveJobRunWire.Type;

const ActiveJobRuns = Schema.Array(ActiveJobRunWire);

// Fails closed: a network hiccup or a decode failure reads as "nothing running" rather than
// surfacing an error state on every hovered row — this indicator is advisory, never load-bearing.
const fetchActiveJobRuns = (documentId: DocumentId) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const response = yield* client.execute(
      HttpClientRequest.get("/api/curricula/job-runs").pipe(
        HttpClientRequest.setUrlParam("documentId", documentId),
      ),
    );
    return yield* HttpClientResponse.schemaBodyJson(ActiveJobRuns)(response);
  }).pipe(
    Effect.scoped,
    Effect.catchAll(() => Effect.succeed<readonly ActiveJobRun[]>([])),
  );

// A short-TTL, fetch-once-then-cache Map keyed by `documentId`, shared by every row in the list —
// module scope rather than component state, so all `AsyncWorkIndicator` instances mounted for the
// same list (one per row) hit the same cache without a context/provider just to carry it. This is
// what keeps repeat hover over the same row (or a re-render of the whole list) from re-issuing the
// request every time; the TTL is short because "running" can flip to "succeeded" between hovers.
const CACHE_TTL_MS = 15_000;
const cache = new Map<
  string,
  { readonly fetchedAt: number; readonly result: Promise<readonly ActiveJobRun[]> }
>();

// Exported for `GenerationProgress` below (and `collab-document.tsx`'s pending branch, which
// mounts it): the same cache this indicator's own hover trigger uses, so a list-page hover and the
// document's own page never issue independent requests for the same run.
export const loadActiveJobRuns = (documentId: DocumentId): Promise<readonly ActiveJobRun[]> => {
  const cached = cache.get(documentId);
  if (cached !== undefined && Date.now() - cached.fetchedAt < CACHE_TTL_MS) return cached.result;
  const result = runtime.runPromise(fetchActiveJobRuns(documentId));
  cache.set(documentId, { fetchedAt: Date.now(), result });
  return result;
};

export const LIFECYCLE_LABEL: Record<ActiveJobRun["lifecycle"], string> = {
  queued: "Queued",
  "pending-approval": "Waiting for approval",
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
  cancelled: "Cancelled",
};

// One run's own entry in the popover — deliberately reads only `lifecycle`/`tasks`, never `kind`:
// that is the whole point of generalising over `kind` (R5) rather than branching per pipeline.
const JobRunEntry = ({ run }: { readonly run: ActiveJobRun }) => {
  const total = run.tasks.length;
  const done = run.tasks.filter((task) => task.state === "succeeded").length;
  return (
    <li className="flex items-center gap-2 whitespace-nowrap text-xs text-zinc-600">
      <span className="relative flex h-3 w-3 shrink-0 items-center justify-center" aria-hidden>
        <span className="absolute inset-0 animate-spin rounded-full border-2 border-brand/25 border-t-brand" />
      </span>
      <span>
        {LIFECYCLE_LABEL[run.lifecycle]}
        {total > 0 ? ` — ${done}/${total} tasks` : ""}
      </span>
    </li>
  );
};

// Mounted once per row (`curricula-index.tsx`). Fetches nothing until the row is first hovered or
// focused, then caches the answer at module scope (above) so a second hover on the same row is
// free. Renders nothing at all — no badge, no glyph — until that fetch resolves with at least one
// non-terminal run; a document with nothing running produces no DOM output.
export const AsyncWorkIndicator = ({ documentId }: { readonly documentId: DocumentId }) => {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState<readonly ActiveJobRun[] | undefined>(undefined);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // No local "already fetched" gate: `loadActiveJobRuns`'s module-scoped, 15s-TTL cache (above) is
  // what de-dupes repeat hovers on the same row. Gating here on a ref that's never cleared would
  // latch the first response forever — a "Running" row would never learn the job finished.
  const ensureLoaded = () => {
    loadActiveJobRuns(documentId).then((result) => {
      if (mounted.current) setRuns(result);
    });
  };

  const handleOpen = () => {
    ensureLoaded();
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const hasActiveRuns = runs !== undefined && runs.length > 0;

  return (
    <button
      type="button"
      // A trigger, not a control — nothing to click, only to hover/focus — so it carries no
      // `onClick`. A real `<button>` rather than a `span`+`tabIndex` gets keyboard focusability and
      // the a11y-appropriate interactive role for free, which is what the "opens on Tab focus"
      // requirement (R5) rests on.
      aria-label="Background activity"
      className="relative inline-flex h-4 w-4 shrink-0 cursor-default items-center justify-center bg-transparent p-0"
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      onFocus={handleOpen}
      onBlur={handleClose}
    >
      {hasActiveRuns ? (
        <span className="relative flex h-3 w-3 items-center justify-center" aria-hidden>
          <span className="absolute inset-0 animate-spin rounded-full border-2 border-brand/25 border-t-brand" />
        </span>
      ) : null}
      {open && hasActiveRuns ? (
        <ul
          role="status"
          data-testid="async-work-indicator"
          className="absolute left-1/2 top-full z-10 mt-1 flex -translate-x-1/2 flex-col gap-1 rounded-md border border-zinc-200 bg-white p-2 shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
        >
          {runs.map((run, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: runs carries no stable id on the wire
            <JobRunEntry key={index} run={run} />
          ))}
        </ul>
      ) : null}
    </button>
  );
};

// A curriculum-generation run's own `JobRunStore` write always carries `tasks: []`
// (`unit-plan-generator-client.ts`'s `withRunProgress` deliberately never populates it for this
// pipeline — the per-task graph isn't one of `LaunchTask.job`'s three named Databricks jobs), so
// there is no completion fraction to read. This is a hardcoded, un-configurable ASSUMPTION of how
// long a run typically takes, not a value derived from any job metadata.
const EXPECTED_GENERATION_DURATION_MS = 45 * 60_000;
// Strictly greater than `loadActiveJobRuns`'s own 15s cache TTL, so each tick's re-fetch actually
// reaches the network instead of replaying a response that is already up to 15s stale.
const POLL_INTERVAL_MS = 20_000;

const formatElapsed = (elapsedMs: number): string => {
  const mins = Math.floor(elapsedMs / 60_000);
  if (mins < 1) return "just started";
  if (mins === 1) return "1 minute ago";
  return `${mins} minutes ago`;
};

// Polls `loadActiveJobRuns` on an interval and hands back this document's own curriculum-generation
// run (if any) plus the clock tick driving the elapsed-time calculation below — shared by
// `GenerationProgress` (the document page) and `RowGenerationProgress` (the list row), U8/R2's "same
// bar in both places" requirement, so the two never drift into separately-tuned polling loops.
const useGenerationRun = (documentId: DocumentId) => {
  const [runs, setRuns] = useState<readonly ActiveJobRun[] | undefined>(undefined);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const poll = () => {
      loadActiveJobRuns(documentId).then((result) => {
        if (mounted.current) setRuns(result);
      });
      if (mounted.current) setNowMs(Date.now());
    };
    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      mounted.current = false;
      clearInterval(interval);
    };
  }, [documentId]);

  return { run: runs?.find((candidate) => candidate.kind === "curriculum-generation"), nowMs };
};

// The linear, time-estimate-based fraction and status text for one run — pure, so both mounts of the
// bar (full-size document page, compact list row) derive identical numbers from identical inputs.
const progressOf = (run: ActiveJobRun, nowMs: number) => {
  const elapsedMs = Math.max(0, nowMs - DateTime.toEpochMillis(run.startedAt));
  const overdue = elapsedMs > EXPECTED_GENERATION_DURATION_MS;
  const percent = Math.min(97, (elapsedMs / EXPECTED_GENERATION_DURATION_MS) * 100);
  const statusLabel = overdue
    ? "Taking longer than usual…"
    : `${LIFECYCLE_LABEL[run.lifecycle]} — started ${formatElapsed(elapsedMs)}`;
  return { percent, statusLabel };
};

// The bar itself — `undefined` percent renders the indeterminate (no `JobRunStore` row yet) pulse,
// otherwise a determinate fraction. `compact` drops the height for the list row, where the bar sits
// inline in an already-dense card rather than its own bordered block.
const GenerationProgressBar = ({
  percent,
  compact = false,
}: {
  readonly percent: number | undefined;
  readonly compact?: boolean;
}) => {
  const className = `w-full overflow-hidden rounded-full bg-zinc-200 ${compact ? "h-1" : "h-1.5"}`;
  if (percent === undefined) {
    return (
      <div role="progressbar" aria-label="Curriculum generation progress" className={className}>
        <div className="h-full w-1/3 animate-pulse rounded-full bg-brand/60" />
      </div>
    );
  }
  return (
    <div
      role="progressbar"
      aria-label="Curriculum generation progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(percent)}
      className={className}
    >
      <div
        className="h-full rounded-full bg-brand transition-[width]"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
};

// U8/R6: the pending document's OWN page, not just the list row's hover popover — mounted
// unconditionally by `collab-document.tsx` whenever `bootstrap.generationStatus === "pending"`
// (never gated on this fetch succeeding: a `JobRunStore` row can be legitimately absent — an
// in-memory store wiped by a restart, a transient 5xx — while the document is still genuinely
// pending, so this degrades to a text-only, indeterminate state rather than disappearing). Always
// visible, unlike `AsyncWorkIndicator`'s hover/focus trigger — that treatment fits a list of many
// rows; a document that IS the pending run gets the state shown outright.
export const GenerationProgress = ({ documentId }: { readonly documentId: DocumentId }) => {
  const { run, nowMs } = useGenerationRun(documentId);
  if (run === undefined) {
    return (
      <div className="not-prose mb-6 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-600">
        {/* Distinct wording from `collab-document.tsx`'s sr-only `generation-live-region` (also
            "Generating curriculum…") — that text is the one-shot a11y announcement of the STATE
            change; this is the always-visible, sighted-only status line sitting beside the bar. */}
        <p>Curriculum generation under way…</p>
        <div className="mt-2">
          <GenerationProgressBar percent={undefined} />
        </div>
      </div>
    );
  }

  const { percent, statusLabel } = progressOf(run, nowMs);

  return (
    <div className="not-prose mb-6 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-600">
      <p>{statusLabel}</p>
      <div className="mt-2">
        <GenerationProgressBar percent={percent} />
      </div>
    </div>
  );
};

// R2 (CON-1316): the SAME linear, time-estimate-based bar as `GenerationProgress` above, sized for a
// list card rather than a full document page — mounted by `curricula-index.tsx` for a
// `generationStatus: "pending"` row. Renders nothing until a run is actually found (no bordered block,
// no indeterminate pulse) — a card is dense enough without a placeholder bar for the gap between the
// row appearing and the first poll landing.
export const RowGenerationProgress = ({ documentId }: { readonly documentId: DocumentId }) => {
  const { run, nowMs } = useGenerationRun(documentId);
  if (run === undefined) return null;
  const { percent } = progressOf(run, nowMs);
  return <GenerationProgressBar percent={percent} compact />;
};
